import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Paper, Tooltip } from '@mui/material'
import moment from 'moment'
import { getLookbackCoverage } from 'api'
import { isInsuranceLogin } from 'tools/auth'
import { useFeatureFlag } from 'contexts/ReportingContext'
import { CARRIER_LOOKBACK_FLAG, LOOKBACK_HIDDEN_REASONS } from 'constants/reporting'
import { reportException } from 'tools/logs'
import { LookbackCoverage } from 'interfaces/ISummaryReport'
import strings from 'strings'
import { TypoGraph } from 'components'
import LookbackModal from '../lookbackModal'

const c = strings.SUMMARY_REPORT.DATA_COVERAGE

const monthLabel = (date: string | null) =>
  date ? moment(date, 'YYYY-MM-DD').format('MM/YYYY') : '-'

const HistoryIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 3v6h6" />
  </svg>
)

const DataCoverage: React.FC = () => {
  const featureEnabled = useFeatureFlag(CARRIER_LOOKBACK_FLAG)
  const [coverage, setCoverage] = useState<LookbackCoverage | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const carrierId: any = localStorage.getItem('carrierId') || '1'
  const codeType: any = localStorage.getItem('carrierCodeType') || 1
  const codeValue: any = localStorage.getItem('carrierCodeValue') || 1

  const loadCoverage = useCallback(() => {
    getLookbackCoverage(carrierId, codeType, codeValue)
      .then((res) => setCoverage(res))
      .catch((error) => reportException(error))
  }, [carrierId, codeType, codeValue])

  useEffect(() => {
    if (featureEnabled && isInsuranceLogin()) {
      loadCoverage()
    }
  }, [featureEnabled, loadCoverage])

  const handleRequested = useCallback(() => {
    setModalOpen(false)
    loadCoverage()
  }, [loadCoverage])

  // Nothing to retrieve from at all — not a wait — so the control is not shown rather than disabled.
  if (
    !featureEnabled ||
    !isInsuranceLogin() ||
    !coverage?.coverageStart ||
    LOOKBACK_HIDDEN_REASONS.includes(coverage.blockedReason ?? '')
  ) {
    return null
  }

  const blockedCopy = coverage.blockedReason
    ? (c.BLOCKED as Record<string, string>)[coverage.blockedReason]
    : undefined
  const coveredMonths =
    moment(coverage.coverageEnd ?? undefined).diff(moment(coverage.coverageStart), 'months') + 1
  const buyableMonths = moment(coverage.coverageStart).diff(
    moment(coverage.earliestSelectableDate ?? undefined),
    'months'
  )
  const havePercent = Math.round((coveredMonths / (coveredMonths + buyableMonths)) * 100)

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            height: 30,
            borderRadius: '5px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'grid',
              placeItems: 'center',
              fontSize: '11.5px',
              fontWeight: 600,
              color: 'text.secondary',
              backgroundImage:
                'repeating-linear-gradient(135deg, #f8f8f8, #f8f8f8 6px, #ededed 6px, #ededed 7px)',
            }}
          >
            {`${c.NO_DATA_BEFORE}${monthLabel(coverage.coverageStart)}`}
          </Box>
          <Box
            sx={{
              flex: `0 0 ${havePercent}%`,
              display: 'grid',
              placeItems: 'center',
              fontSize: '11.5px',
              fontWeight: 700,
              letterSpacing: '.3px',
              color: 'primary.contrastText',
              backgroundColor: 'primary.main',
            }}
          >
            {`${c.REPORTING_HISTORY} · ${monthLabel(coverage.coverageStart)} – ${monthLabel(coverage.coverageEnd)}`}
          </Box>
        </Box>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: 'grey.50',
          borderLeft: '3px solid',
          borderLeftColor: 'primary.main',
          borderRadius: '8px',
          p: '15px 18px',
          mt: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 'none', color: 'primary.main', display: 'flex' }}>
          <HistoryIcon />
        </Box>
        <Box sx={{ flex: '1 1 340px' }}>
          <TypoGraph variant="body2" sx={{ fontWeight: 700 }}>
            {`${c.TITLE}${moment(coverage.coverageStart).format('MMMM YYYY')}`}
          </TypoGraph>
          <TypoGraph
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {c.SUBTITLE}
          </TypoGraph>
        </Box>
        <Tooltip title={blockedCopy ?? ''} arrow>
          {/* A disabled button takes no focus, so the wrapper carries it and the reason stays reachable. */}
          <span tabIndex={coverage.enabled ? -1 : 0}>
            <Button
              variant="contained"
              color="primary"
              data-testid="data-coverage-request"
              disabled={!coverage.enabled}
              onClick={() => setModalOpen(true)}
            >
              {c.ACTION}
            </Button>
          </span>
        </Tooltip>
      </Paper>

      {modalOpen && (
        <LookbackModal
          coverage={coverage}
          carrierId={carrierId}
          codeType={codeType}
          codeValue={codeValue}
          onClose={() => setModalOpen(false)}
          onRequested={handleRequested}
        />
      )}
    </>
  )
}

export default DataCoverage
